import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Facebook, Instagram, Mail, MapPin, Phone, Youtube } from "lucide-react";
import { useSite } from "../../lib/site";
import { pageHref } from "../../lib/format";

/** Max links shown per column before falling back to "View all". */
const PER_COL = 5;

const DAY_ABBR = [
  ["Monday", "Mon"], ["Tuesday", "Tue"], ["Wednesday", "Wed"], ["Thursday", "Thu"],
  ["Friday", "Fri"], ["Saturday", "Sat"], ["Sunday", "Sun"],
];

/**
 * Squash the opening-hours table into a single readable line, e.g.
 * "Mon-Thu 7:30am-4:30pm · Fri 7:30am-3:00pm · Closed Sat-Sun".
 */
function hoursLine(hours) {
  if (!Array.isArray(hours) || hours.length === 0) return "";
  const abbr = (s = "") =>
    DAY_ABBR.reduce((acc, [long, short]) => acc.replaceAll(long, short), s).replace(/\s*-\s*/g, "-");

  const open = [];
  const closed = [];
  hours.forEach((h) => {
    const time = (h.time || "").trim();
    if (/closed/i.test(time)) closed.push(abbr(h.days));
    else open.push(`${abbr(h.days)} ${time.replace(/\s*-\s*/g, "-")}`);
  });
  if (closed.length) open.push(`Closed ${closed.join(", ")}`);
  return open.join(" · ");
}

function LinkCol({ title, links, viewAllTo, testid }) {
  if (!links.length) return null;
  const shown = links.slice(0, PER_COL);
  const hidden = links.length - shown.length;
  return (
    <div data-testid={testid}>
      <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider gold-text">{title}</h4>
      <ul className="space-y-1.5">
        {shown.map((k) => (
          <li key={k.slug || k.label}>
            <Link
              to={pageHref(k.slug)}
              className="text-sm text-[color:var(--nq-stone)] transition hover:text-[color:var(--nq-gold)]"
            >
              {k.label}
            </Link>
          </li>
        ))}
        {hidden > 0 && (
          <li>
            <Link
              to={viewAllTo}
              className="inline-flex items-center gap-1 text-sm font-medium text-[color:var(--nq-gold)] hover:underline"
            >
              View all <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </li>
        )}
      </ul>
    </div>
  );
}

export default function Footer() {
  const { site, nav } = useSite();
  const contact = site?.contact || {};
  const branding = site?.branding || {};
  const phone = contact.phone || "(07) 4774 8859";
  const logo = "/nq-springs-logo-v3.png";
  const socials = contact.socials || {};
  const hasSocials = socials.facebook || socials.instagram || socials.youtube;

  // Parent sections (Suspension, GVM, Accessories, Trucks, ...) and the
  // standalone pages. Both are capped so the footer stays a single row.
  const categories = nav.filter((n) => (n.children || []).length > 0 && n.label?.trim());
  const useful = nav.filter(
    (n) => (n.children || []).length === 0 && n.label?.trim() && n.slug !== "home",
  );

  const hrs = hoursLine(contact.hours);

  return (
    <footer
      className="mt-16 border-t"
      style={{ borderColor: "rgba(255,255,255,0.08)", background: "var(--nq-ink)" }}
      data-testid="site-footer"
    >
      <div className="gold-rule" />
      <div className="container-nq py-8">
        {/* Single row on desktop: brand | categories | useful links | contact */}
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {/* ---- brand ---- */}
          <div>
            {logo ? (
              <img src={logo} alt="NQ Springs" className="h-11 w-auto" data-testid="footer-logo" />
            ) : (
              <span className="text-xl font-bold">
                NQ<span className="gold-text">SPRINGS</span>
              </span>
            )}
            <p className="eyebrow mt-3">{branding.tagline}</p>
            <p className="mt-2 max-w-xs text-sm leading-relaxed text-[color:var(--nq-stone)]">
              {branding.description}
            </p>
            {hasSocials && (
              <div className="mt-4 flex items-center gap-2">
                {socials.facebook && (
                  <a href={socials.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="rounded-lg border p-2 hover:border-[color:var(--nq-gold)]" style={{ borderColor: "rgba(255,255,255,0.12)" }}>
                    <Facebook className="h-4 w-4" />
                  </a>
                )}
                {socials.instagram && (
                  <a href={socials.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="rounded-lg border p-2 hover:border-[color:var(--nq-gold)]" style={{ borderColor: "rgba(255,255,255,0.12)" }}>
                    <Instagram className="h-4 w-4" />
                  </a>
                )}
                {socials.youtube && (
                  <a href={socials.youtube} target="_blank" rel="noreferrer" aria-label="YouTube" className="rounded-lg border p-2 hover:border-[color:var(--nq-gold)]" style={{ borderColor: "rgba(255,255,255,0.12)" }}>
                    <Youtube className="h-4 w-4" />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* ---- nav columns ---- */}
          <LinkCol
            title="Shop by category"
            links={categories}
            viewAllTo="/site-index"
            testid="footer-categories"
          />
          <LinkCol
            title="Useful links"
            links={useful}
            viewAllTo="/site-index"
            testid="footer-useful"
          />

          {/* ---- contact ---- */}
          <div data-testid="footer-contact">
            <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider gold-text">Get in touch</h4>
            <ul className="space-y-2 text-sm text-[color:var(--nq-stone)]">
              <li className="flex gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 gold-text" />
                <span>{contact.address}</span>
              </li>
              <li className="flex gap-2.5">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 gold-text" />
                <a href={`tel:${phone.replace(/[^\d+]/g, "")}`} className="hover:text-[color:var(--nq-gold)]">
                  {phone}
                </a>
              </li>
              {contact.email && (
                <li className="flex gap-2.5">
                  <Mail className="mt-0.5 h-4 w-4 shrink-0 gold-text" />
                  <a href={`mailto:${contact.email}`} className="break-all hover:text-[color:var(--nq-gold)]">
                    {contact.email}
                  </a>
                </li>
              )}
              {hrs && (
                <li className="flex gap-2.5" data-testid="footer-hours">
                  <Clock className="mt-0.5 h-4 w-4 shrink-0 gold-text" />
                  <span>{hrs}</span>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
        <div className="container-nq flex flex-col items-center justify-between gap-2 py-4 text-xs text-[color:var(--nq-stone)] sm:flex-row">
          <p>&copy; {new Date().getFullYear()} NQ Springs & 4x4 Accessories. All rights reserved.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1" data-testid="footer-policy-links">
            <Link to="/returns-refunds" className="hover:text-[color:var(--nq-gold)]">Returns & Refunds</Link>
            <Link to="/shipping-delivery" className="hover:text-[color:var(--nq-gold)]">Shipping</Link>
            <Link to="/terms-of-sale" className="hover:text-[color:var(--nq-gold)]">Terms of Sale</Link>
            <Link to="/privacy-policy" className="hover:text-[color:var(--nq-gold)]">Privacy</Link>
            <Link to="/site-index" className="hover:text-[color:var(--nq-gold)]">Site index</Link>
            <Link to="/contact-us" className="hover:text-[color:var(--nq-gold)]">Contact</Link>
            <Link to="/admin" className="hover:text-[color:var(--nq-gold)]" data-testid="footer-admin-link">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
